'use client';

import { ProgressProvider } from '@bprogress/next/app';
import { Suspense, useCallback, useEffect, useState } from 'react';
import Analytics from '@/components/entities/analytics/Analytics';
import OneSignalInit from '@/components/entities/analytics/components/OneSIgnal/OneSignal';
import { AuthErrorHandler } from '@/components/features/layout/AuthErrorHandler';
import PwaBootstrap from '@/components/features/pwa/PwaBootstrap';
import { PwaInstallProvider } from '@/components/features/pwa/PwaInstallProvider';
import GlobalContainer from '@/components/shared/GlobalContainer';
import { useUserDeactivationSocket } from '@/hooks/socket/useUserDeactivationSocket';
import { useDocumentVisibility } from '@/hooks/useDocumentVisibility';
import { useIsThisPage } from '@/hooks/useIsThisPage';
import { useLobbyScrollRestoration } from '@/hooks/useLobbyScrollRestoration';
import { AppRoutes } from '@/i18n/constants';
import ModalsProvider from '@/modals/components/ModalProvider';
import VpnDetectedModalGuard from '@/modals/components/VpnDetectedModalGuard';
import { getFirstVisitRegistration } from '@/modals/modules/auth/RegisterModal/visitRegistration.utils';
import openModal from '@/modals/utils/openModal';
import { cn } from '@/utils/cn';
import Footer from './Footer/Footer';
import Header from './Header/Header';
import Navigation from './Navigation';

export default function ClientLayout({ hideNavigation = false, children }: { hideNavigation?: boolean; children: React.ReactNode }) {
  const [isMenuMinify] = useState(false);
  const [isOpenSideBar, setIsOpenSideBar] = useState(false);

  useUserDeactivationSocket();
  useLobbyScrollRestoration();

  const onVisible = useCallback(() => {
    const value = getFirstVisitRegistration();
    if (value !== undefined && value) {
      openModal('login', { isMakeVisitAfterRegistration: true });
    }
  }, []);

  useDocumentVisibility({ onVisible });

  const isContainerFluid = useIsThisPage(AppRoutes.home, 'exact');
  const isGamePage = useIsThisPage(AppRoutes.gameBase);
  const isGamificationMobileCardPage = useIsThisPage([
    AppRoutes.gamificationLevelUp,
    AppRoutes.gamificationStatusUp,
  ]);
  const [isGameStarted, setIsGameStarted] = useState(false);

  // Lets modals (outside the Navigation tree) request a sidebar close.
  useEffect(() => {
    if (!isOpenSideBar) {
      return;
    }

    const handleSidebarClose = () => setIsOpenSideBar(false);

    window.addEventListener('sidebar:close', handleSidebarClose);
    return () => window.removeEventListener('sidebar:close', handleSidebarClose);
  }, [isOpenSideBar]);

  useEffect(() => {
    if (!isGamePage) {
      setIsGameStarted(false);
      return;
    }

    const handleGameStarted = (event: Event) => {
      setIsGameStarted((event as CustomEvent<boolean>).detail === true);
    };

    window.addEventListener('game:started', handleGameStarted);
    return () => {
      window.removeEventListener('game:started', handleGameStarted);
      setIsGameStarted(false);
    };
  }, [isGamePage]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      console.warn(`%cSTOP!`, 'color: red; font-size: 40px; font-weight: 700');

      console.warn(
        `%cThis browser feature is intended for developers. If you are asked to copy and paste something here to activate a feature of sweepking.games or to "hack" someone else's account, please be aware that this is a scam to gain access to your sweepking.games account.`,
        'font-size: 16px;',
      );
    }
  }, []);

  return (
    <PwaInstallProvider>
      <ProgressProvider
        height="4px"
        color="var(--progress-bar)"
        options={{ showSpinner: false }}
        shallowRouting
      >
        <Suspense>
          <PwaBootstrap />
        </Suspense>
        <Suspense>
          <OneSignalInit />
        </Suspense>
        <Suspense>
          <ModalsProvider />
        </Suspense>
        <Suspense>
          <VpnDetectedModalGuard />
        </Suspense>

        <main>
          <div className="flex w-full 3xl:justify-center min-h-full pt-16">
            <div className="flex 3xl:max-w-388.5 w-full min-h-full">
              {!hideNavigation && (
                <>
                  <Navigation
                    isHomePage={isContainerFluid}
                    isMenuMinify={isMenuMinify}
                    isOpenSideBar={isOpenSideBar}
                    setIsOpenSideBar={setIsOpenSideBar}
                    hideMobileNav={isGamificationMobileCardPage || (isGamePage && isGameStarted)}
                  />
                  <Header
                    isOpenSideBar={isOpenSideBar}
                    setIsOpenSideBar={setIsOpenSideBar}
                  />
                </>
              )}
              <div className="@container/main 3xl:max-w-322.75 w-full flex flex-col grow min-h-0">
                <GlobalContainer
                  className={cn(
                    '3xl:max-w-none min-w-full',
                    isContainerFluid ? 'contents bg-c900' : '',
                  )}
                >
                  <Suspense>
                    <AuthErrorHandler />
                  </Suspense>
                  {children}
                  <Analytics />
                </GlobalContainer>
                {!hideNavigation && <Footer className="3xl:hidden" />}
                <div id="portal-root" />
              </div>
            </div>
          </div>
          {!hideNavigation && <Footer className="3xl:block hidden 3xl:pb-8 3xl:pt-6" />}
        </main>
      </ProgressProvider>
    </PwaInstallProvider>
  );
}
